import { COMMA, ENTER, SEMICOLON, SPACE } from '@angular/cdk/keycodes';
import {
  Component,
  Inject,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  ADMINISTRATOR_GROUP_NAME,
  IProjectGroup,
} from '@core/state/entities/project-group/project-group.entity';
import {
  emailArrayValidator,
  requiredArrayValidator,
  SimpleNonNullableFormGroup,
  throwErrorExp,
} from '@core/actions/util';
import { Store } from '@ngrx/store';
import {
  EmailInvitationResponseMap,
  projectDetailActions,
} from '@core/actions/project-detail.actions';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { MatChipInputEvent } from '@angular/material/chips';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';

import { filter, map, Observable, pairwise, switchMap, take, tap } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { InviteResponse } from '@core/services/api-client.generated';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  IInfoConfirmationDialogData,
  InfoConfirmationDialogComponent,
} from '@shared/dialogs/info-confirmation-dialog/info-confirmation-dialog.component';
import { createFormValueBehavior } from '@core/utils/form.utils';
import { switchLatestFrom } from '@core/utils/switchLatestFrom';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { ChipsEmailsInputComponent } from '@shared/chips-emails-input/chips-emails-input.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { DebuggerComponent } from '@shared/debugger/debugger.component';
import { DebuggerGroupComponent } from '@shared/debugger-group/debugger-group.component';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { v4 as uuid } from 'uuid';

export interface IInviteMembersDialogData {
  projectGroups: IProjectGroup[];
  projectId: string;
}

export interface Emails {
  emailAddress: string;
}

interface InviteNewMembersFormData {
  inviteeEmails: string[];
  isAdmin: boolean | null;
  message: string;
}

enum DialogStatus {
  infoEntry,
  sending,
  unknownError,
  specificErrorResponse,
  success,
}

const getErrorsFromInvitationReport = (
  emailInvitationErrorResponse: EmailInvitationResponseMap
) =>
  Object.keys(emailInvitationErrorResponse).filter(
    (email) => emailInvitationErrorResponse[email] !== InviteResponse.Invited
  );
const ACCESS_UPDATED = 'Access updated';

const UNKNOWN_ERROR = 'A problem occurred communicating with the server';

@Component({
  selector: 'app-invite-members-dialog',
  templateUrl: './invite-members-dialog.component.html',
  styleUrls: ['./invite-members-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatProgressBarModule,
    MatDialogTitle,
    MatIconModule,
    DebuggerGroupComponent,
    DebuggerComponent,
    FormsModule,
    ReactiveFormsModule,
    MatDialogContent,
    FlexLayoutModule,
    MatFormFieldModule,
    ChipsEmailsInputComponent,
    MatSelectModule,
    MatOptionModule,
    MatInputModule,
    MatDialogActions,
    MatButtonModule,
    AsyncPipe,
  ],
})
export class InviteMembersDialogComponent extends ComponentStore<{
  status: DialogStatus;
  emailResponseReport: EmailInvitationResponseMap | null;
}> {
  @ViewChild('confirmationDialogTemplate')
  confirmationDialogTemplate: TemplateRef<void>;
  inviteNewMemberForm: SimpleNonNullableFormGroup<InviteNewMembersFormData>;

  emailInput: Emails[] = [];
  inviteeEmails$: Observable<string[]>;
  sendingInvitation$: Observable<boolean>;
  adminGroupSelected$: Observable<boolean | null>;
  readonly separatorKeysCodes = [ENTER, SPACE, SEMICOLON, COMMA] as const;

  sendInvitation = this.effect((sendInvitation$: Observable<void>) =>
    sendInvitation$.pipe(
      tap(() => console.log('sendInvitation$ fired')),
      switchLatestFrom(() => [this.inviteeEmails$, this.adminGroupSelected$]),
      tap((val) => console.log('sendInvitation$ concated ', val)),
      switchMap(([, inviteeEmails, adminGroupSelected]) =>
        this._dialog
          .open<
            InfoConfirmationDialogComponent,
            IInfoConfirmationDialogData,
            boolean
          >(InfoConfirmationDialogComponent, {
            data: {
              title: `Share ${
                adminGroupSelected ? 'administrator ' : ''
              }access to this SyRF project`,
              text: `You are sending ${
                inviteeEmails.length === 1 ? 'an invitation' : 'invitations'
              } to`,
              listItems: inviteeEmails,
              informationBox: {
                text: `Anyone holding an invitation will have ${
                  adminGroupSelected ? 'administrator ' : ''
                }access to this project via their
              SyRF account`,
                warning: true,
              },
            },
          })
          .afterClosed()
      ),

      tap((confirmed) => console.log('confirmation dialog closed', confirmed)),
      filter((dialogResponse) => !!dialogResponse),
      tap(() => {
        const formData = this.inviteNewMemberForm.getRawValue();
        this.patchState({
          status: DialogStatus.sending,
        });
        this._store.dispatch(
          projectDetailActions.inviteMembers({
            projectId: this.data.projectId,
            inviteeEmails: formData.inviteeEmails,
            groupIds: formData.isAdmin ? [this.adminProjectGroupId] : [],
            message: formData.message,
            correlationId: uuid(),
          })
        );
      }),
      switchMap(() =>
        this._actions$.pipe(
          ofType(
            projectDetailActions.membersInvitedResponseReceived,
            projectDetailActions.errorInvitingMembers
          ),
          take(1),
          tapResponse(
            (props) => {
              this.patchState(
                props.type === projectDetailActions.errorInvitingMembers.type
                  ? {
                      status: DialogStatus.unknownError,
                      emailResponseReport: null,
                    }
                  : {
                      status:
                        getErrorsFromInvitationReport(props).length > 0
                          ? DialogStatus.specificErrorResponse
                          : DialogStatus.success,
                      emailResponseReport: props,
                    }
              );
              this._dialogRef.close(true);
            },
            () => undefined
          )
        )
      ),
      tap(() => console.log('sendInvitation$ completed'))
    )
  );

  get adminProjectGroupId() {
    return (
      this.data.projectGroups.find(
        (projectGroup) => projectGroup.name === ADMINISTRATOR_GROUP_NAME
      )?.id ?? throwErrorExp('Admin group not found')
    );
  }

  constructor(
    private _fb: FormBuilder,
    private _dialogRef: MatDialogRef<InviteMembersDialogComponent>,
    private _dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: IInviteMembersDialogData,
    private _store: Store,
    private _actions$: Actions,
    private _snackbar: MatSnackBar
  ) {
    super({
      status: DialogStatus.infoEntry,
      emailResponseReport: null,
    });
    this.sendingInvitation$ = this.select((s) => s.status).pipe(
      map((status) => status === DialogStatus.sending)
    );
    this.inviteNewMemberForm = this._fb.nonNullable.group({
      inviteeEmails: new FormControl([] as string[], {
        validators: [emailArrayValidator, requiredArrayValidator],
        nonNullable: true,
      }),
      isAdmin: new FormControl<boolean | null>(false, Validators.required),
      message: '',
    });
    this.inviteeEmails$ = createFormValueBehavior<string[]>(
      this.inviteNewMemberForm.controls.inviteeEmails
    );
    this.adminGroupSelected$ = createFormValueBehavior<boolean | null>(
      this.inviteNewMemberForm.controls.isAdmin
    );
    this.effect(() => this.inviteeEmails$);
    this.effect((_) =>
      this.select((s) => s.status).pipe(
        pairwise(),
        filter(([prev]) => prev === DialogStatus.sending),
        tap(([, curr]) => {
          if (curr === DialogStatus.success) {
            this._snackbar.open(ACCESS_UPDATED);
            this._dialogRef.close(true);
          } else if (curr === DialogStatus.unknownError) {
            this._snackbar.open(UNKNOWN_ERROR);
          } else if (curr === DialogStatus.specificErrorResponse) {
            this._dialogRef.close(true);
          }
        })
      )
    );
  }

  cancel() {
    this.inviteNewMemberForm.reset();
    this._dialogRef.close(false);
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.emailInput.push({ emailAddress: value });
    }
    event.chipInput!.clear();
  }

  remove(email: Emails): void {
    const index = this.emailInput.indexOf(email);

    if (index >= 0) {
      this.emailInput.splice(index, 1);
      if (this.emailInput.length === 0) {
        this.inviteNewMemberForm.setErrors({ invalid: true });
        this.inviteNewMemberForm.controls.inviteeEmails.setErrors({
          required: true,
        });
      }
    }
  }
}
